import React, { FC, ReactNode, memo, useState } from "react";
import { FaInfo } from "react-icons/fa";
import { makeStyles } from "tss-react/mui";
import { Countdown } from "@/components/library/Countdown";
import { Loading } from "@/components/composite/Loading";
import { palette, radius, shadow, spacing } from "@/themes/tokens";
import Pill from "./Pill";
import CountdownTiles from "./CountdownTiles";

export interface LotCardProps {
    variant?: "detail" | "compact";
    imgSrc: string;
    onOpen: () => void;
    lotNumber: string | number;
    title: string;
    description?: string;
    lotFeatures?: any[];

    isActive: boolean;
    validating?: boolean;
    finalizingText?: string;
    soldBannerText?: string;
    now: Date;
    start: Date;
    end?: Date;
    isExtended?: boolean;

    stcStatus?: number | string;
    commission?: number | string;
    showReserve?: boolean;
    reserveMet?: boolean;

    countdownDate?: string;
    countdownHeader?: string;
    getNow: () => number;

    documents?: ReactNode;
    wishlist?: ReactNode;
    onInfoClick?: (event: React.MouseEvent<HTMLElement>) => void;
    infoPopper?: ReactNode;
    pricing?: ReactNode;
    actions?: ReactNode;
}

const stcText = (value: any) => (value?.toString() === "0" ? "STC - No" : value?.toString() === "1" ? "STC - Yes" : value?.toString() === "2" ? "STC - Awaiting" : undefined);
const short = (text?: string) => (text && text.length > 15 ? text.substring(0, 15) + "..." : text);

const useStyles = makeStyles()((theme) => ({
    card: {
        background: palette.surface,
        borderRadius: radius.lg,
        border: `1px solid ${palette.border}`,
        boxShadow: shadow.card,
        overflow: "hidden",
        height: "100%",
        containerType: "inline-size",
    },

    // Detail: image on the left, details on the right (stacks on narrow windows).
    detail: {
        display: "flex",
        gap: spacing.lg,
        padding: spacing.md,
        "@container (max-width: 640px)": { flexDirection: "column" },
    },
    detailImage: { width: "34%", minWidth: 260, flexShrink: 0, "@container (max-width: 640px)": { width: "100%", minWidth: 0 } },
    detailBody: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: spacing.md, padding: "4px 4px 4px 0" },
    detailColumns: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: spacing.lg,
        alignItems: "start",
        "@container (max-width: 820px)": { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    detailActions: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.sm,
        marginTop: "auto",
        "& > *": { flex: "0 1 220px" },
        "@container (max-width: 640px)": { "& > *": { flex: 1 } },
    },

    // Compact: small image beside the status, title and countdown; features and pricing below.
    compact: { padding: spacing.sm, display: "flex", flexDirection: "column", gap: spacing.sm },
    compactTop: { display: "flex", gap: spacing.sm, "@container (max-width: 330px)": { flexDirection: "column" } },
    compactImage: { width: "46%", flexShrink: 0, "@container (max-width: 330px)": { width: "100%" } },
    compactSide: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 },
    compactFooter: { display: "flex", gap: spacing.sm, alignItems: "stretch", "@container (max-width: 330px)": { flexDirection: "column" } },

    imageBox: { position: "relative", borderRadius: radius.md, overflow: "hidden", aspectRatio: "1.78", cursor: "pointer" },
    title: { fontWeight: 700, color: palette.text, lineHeight: 1.3 },
    features: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
    infoButton: {
        width: 22,
        height: 22,
        borderRadius: 11,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: palette.surfaceMuted,
        border: `1px solid ${palette.border}`,
        color: palette.textMuted,
        cursor: "pointer",
        flexShrink: 0,
    },
}));

const LotCard: FC<LotCardProps> = (props) => {
    const {
        variant = "detail", imgSrc, onOpen, lotNumber, title, description, lotFeatures,
        isActive, validating, finalizingText, soldBannerText, now, start, end, isExtended,
        stcStatus, commission, showReserve, reserveMet,
        countdownDate, countdownHeader, getNow,
        documents, wishlist, onInfoClick, infoPopper, pricing, actions,
    } = props;
    const { classes } = useStyles();
    const [aspectRatio, setAspectRatio] = useState(0);
    const compact = variant === "compact";
    const small = compact ? "small" : "regular";

    const status = (
        validating ? <Pill size={small} dot variant="solid" tone="dark" label="Validating" />
            : !isActive ? <Pill size={small} dot variant="solid" tone="dark" label="Unavailable" />
                : now < start ? <Pill size={small} dot variant="solid" tone="dark" label="Awaiting" />
                    : end && now > end ? <Pill size={small} dot variant="solid" tone="danger" label="Completed" />
                        : isExtended ? <Pill size={small} dot variant="solid" tone="warning" label="Extended" />
                            : <Pill size={small} dot variant="solid" tone="success" label="Open" />
    );

    const reserveBadge = showReserve && isActive && !!end && now < end && (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                alignSelf: "flex-start",
                padding: compact ? "1px 7px" : "2px 9px",
                borderRadius: radius.pill,
                background: palette.surface,
                border: `1px solid ${reserveMet ? palette.success : palette.danger}`,
                color: reserveMet ? palette.success : palette.danger,
                fontSize: compact ? 10 : 12,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
            }}
        >
            {reserveMet ? "Reserve Met" : "Reserve Not Met"}
        </span>
    );

    const infoButton = onInfoClick && (
        <button type="button" onClick={onInfoClick} aria-label="Info" className={classes.infoButton}>
            <FaInfo size={10} />
        </button>
    );

    const tiles = (size: "regular" | "compact", headerRight?: ReactNode) =>
        countdownDate ? (
            <Countdown
                now={getNow}
                date={countdownDate}
                renderer={({ days, hours, minutes, seconds, completed }) => (
                    <CountdownTiles size={size} header={countdownHeader} days={days} hours={hours} minutes={minutes} seconds={seconds} completed={completed} headerRight={headerRight} />
                )}
            />
        ) : (
            <CountdownTiles size={size} header={countdownHeader} days={0} hours={0} minutes={0} seconds={0} placeholder headerRight={headerRight} />
        );

    const features = (lotFeatures ?? [])
        .slice()
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
        .slice(0, 4);

    const featureChips = features.length > 0 && (
        <div className={classes.features}>
            {features.map((item, index) => (
                <div
                    key={index}
                    style={{
                        minHeight: compact ? 34 : 42,
                        border: `1px solid ${palette.border}`,
                        background: palette.surfaceMuted,
                        borderRadius: radius.pill,
                        padding: "2px 10px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: compact ? 10.5 : 12,
                        color: palette.textMuted,
                        textAlign: "center",
                        lineHeight: 1.3,
                        minWidth: 0,
                    }}
                >
                    <span style={{ fontWeight: 600, color: palette.text, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{short(item?.key ?? item?.name)}</span>
                    <span style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{short(item?.value)}</span>
                </div>
            ))}
        </div>
    );

    const image = (showStatus: boolean, showWishlist: boolean) => (
        <div className={classes.imageBox} style={{ backgroundColor: aspectRatio >= 1.77 ? palette.surface : palette.surfaceMuted }} onClick={onOpen}>
            <img
                src={imgSrc}
                alt=""
                onLoad={(e) => e.currentTarget.naturalHeight && setAspectRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: validating ? 0.3 : 1 }}
            />
            {validating && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9 }}>
                    <Loading loading={true} loadingText={finalizingText} size={compact ? 22 : 28} loadingTextStyle={{ fontSize: 12, color: palette.text }} />
                </div>
            )}
            {!validating && soldBannerText && (
                <div style={{ position: "absolute", left: 8, right: 8, top: "50%", transform: "translateY(-50%)", zIndex: 9 }}>
                    <div style={{ background: palette.danger, color: "#fff", borderRadius: 50, textAlign: "center", fontWeight: 700, fontSize: compact ? 12 : 16, padding: 2 }}>{soldBannerText}</div>
                </div>
            )}
            {showStatus && <div style={{ position: "absolute", top: spacing.sm, left: spacing.sm, pointerEvents: "none" }}>{status}</div>}
            {showWishlist && isActive && wishlist && (
                <div style={{ position: "absolute", top: spacing.sm, right: spacing.sm }} onClick={(e) => e.stopPropagation()}>
                    {wishlist}
                </div>
            )}
        </div>
    );

    if (compact) {
        return (
            <div className={classes.card}>
                <div className={classes.compact}>
                    <div className={classes.compactTop}>
                        <div className={classes.compactImage}>{image(false, true)}</div>
                        <div className={classes.compactSide}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                                {status}
                                {stcText(stcStatus) && <Pill size="small" label={stcText(stcStatus)} />}
                                <Pill size="small" tone="primary" label={"BC " + commission + "%"} />
                                {infoButton}
                                {infoPopper}
                            </div>
                            <div title={title} style={{ fontSize: 13, ...ellipsis }} className={classes.title}>
                                <span style={{ color: palette.textFaint, fontWeight: 600 }}>{lotNumber} | </span>
                                {title}
                            </div>
                            {tiles("compact", reserveBadge)}
                        </div>
                    </div>
                    {featureChips}
                    <div className={classes.compactFooter}>
                        <div style={{ flex: 1, minWidth: 0 }}>{isActive && pricing}</div>
                        {actions && <div className="pb-card-actions pb-card-actions--compact" style={{ flex: 1, justifyContent: "flex-end" }}>{actions}</div>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.card}>
            <div className={classes.detail}>
                <div className={classes.detailImage}>{image(true, true)}</div>
                <div className={classes.detailBody}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: spacing.sm }}>
                        <div style={{ flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                            {stcText(stcStatus) && <Pill label={stcText(stcStatus)} />}
                            <Pill tone="primary" label={"Buyer's Commission " + commission + "%"} />
                            {reserveBadge}
                            {infoButton}
                            {infoPopper}
                        </div>
                        {isActive && documents}
                    </div>

                    <div className={classes.detailColumns}>
                        <div style={{ display: "flex", flexDirection: "column", gap: spacing.md, minWidth: 0 }}>
                            <div title={title} style={{ fontSize: 17, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} className={classes.title}>
                                <span style={{ color: palette.textFaint }}>{lotNumber} | </span>
                                {title}
                            </div>
                            {tiles("regular")}
                            {description && (
                                <div style={{ fontSize: 13, lineHeight: "18px", color: palette.textMuted, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                                    {description.length > 100 ? description.substring(0, 100) + "..." : description}
                                </div>
                            )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: spacing.md, minWidth: 0 }}>
                            {isActive && pricing}
                            {featureChips}
                        </div>
                    </div>

                    {actions && <div className={"pb-card-actions " + classes.detailActions}>{actions}</div>}
                </div>
            </div>
        </div>
    );
};

const ellipsis: React.CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

export default memo(LotCard);
